import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card,
  Button,
  DataTable,
  DataTableColumn,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi, PhoneCall } from './voiceClient';

interface Props { storeId: string; }

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [calls, setCalls] = React.useState<PhoneCall[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [twilioNumber, setTwilioNumber] = React.useState('');
  const [transferNumber, setTransferNumber] = React.useState('');
  const [savingNumber, setSavingNumber] = React.useState(false);
  const [savingTransfer, setSavingTransfer] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const cfg: any = await voiceApi.getConfig(getIdToken, storeId);
        if (cfg.twilioPhoneNumber) setTwilioNumber(cfg.twilioPhoneNumber);
        if (cfg.managerTransferNumber) setTransferNumber(cfg.managerTransferNumber);
      } catch { /* no config yet — defaults apply */ }
    })();
  }, [getIdToken, storeId]);

  const saveField = async (patch: Record<string, string>, setter: (v: boolean) => void, label: string) => {
    setter(true);
    try {
      await voiceApi.updateConfig(getIdToken, storeId, patch as any);
      show({ tone: 'success', title: `${label} saved` });
    } catch (err: any) {
      show({ tone: 'error', title: 'Save failed', description: err.message });
    } finally {
      setter(false);
    }
  };

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await voiceApi.listPhoneCalls(getIdToken, storeId, 50);
      setCalls(r.calls);
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to load calls', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [getIdToken, storeId, show]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const columns: DataTableColumn<PhoneCall>[] = [
    { key: 'startedAt', header: 'When', accessor: (r) => new Date(r.startedAt).toLocaleString(), sortable: true, compare: (a, b) => a.startedAt.localeCompare(b.startedAt) },
    { key: 'from', header: 'From', accessor: (r) => r.fromNumber || '—' },
    { key: 'to', header: 'To', accessor: (r) => r.toNumber || '—' },
    { key: 'status', header: 'Status', accessor: (r) => r.status },
    { key: 'outcome', header: 'Outcome', accessor: (r) => r.callOutcome || '—' },
    { key: 'transfer', header: 'Transferred to', accessor: (r) => r.transferredTo || '—' }
  ];

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Phone AI assistant</h1>
      <p style={{ color: 'var(--bso-text-secondary)', maxWidth: 640, marginBottom: 'var(--bso-space-8)' }}>
        Configure the Twilio number and transfer rules for this store, and review
        recent inbound calls handled by the xAI Voice Agent.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--bso-space-6)', marginBottom: 'var(--bso-space-8)' }}>
        <Card>
          <Card.Header title="Twilio number" subtitle="Set the inbound DID for this store" />
          <Card.Body>
            <TextInput
              label="Twilio phone number (E.164)"
              value={twilioNumber}
              onChange={(e) => setTwilioNumber(e.target.value)}
              placeholder="+15551234567"
              hint="Configure Twilio webhook → POST /api/phone/calls?storeId={storeId}"
            />
          </Card.Body>
          <Card.Footer>
            <Button
              onClick={() => saveField({ twilioPhoneNumber: twilioNumber.trim() }, setSavingNumber, 'Twilio number')}
              loading={savingNumber}
              disabled={!twilioNumber.trim()}
            >
              Save number
            </Button>
          </Card.Footer>
        </Card>

        <Card>
          <Card.Header title="Manager transfer" />
          <Card.Body>
            <TextInput
              label="Manager cell phone (E.164)"
              value={transferNumber}
              onChange={(e) => setTransferNumber(e.target.value)}
              placeholder="+15558675309"
              hint="Caller asks for a manager → bridged here via Twilio Dial."
            />
          </Card.Body>
          <Card.Footer>
            <Button
              onClick={() => saveField({ managerTransferNumber: transferNumber.trim() }, setSavingTransfer, 'Transfer target')}
              loading={savingTransfer}
              disabled={!transferNumber.trim()}
            >
              Save target
            </Button>
          </Card.Footer>
        </Card>
      </div>

      <Card>
        <Card.Header
          title="Recent calls"
          subtitle={loading ? 'Loading…' : `${calls.length} call(s)`}
          actions={<Button size="sm" variant="ghost" onClick={refresh}>Refresh</Button>}
        />
        <Card.Body>
          <DataTable<PhoneCall>
            columns={columns}
            rows={calls}
            getRowKey={(r) => r.callId}
            pageSize={20}
            emptyState={loading ? 'Loading…' : 'No phone calls recorded yet.'}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export const PhoneAIConfigPanel: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
